import LoadingPage from '../../components/LoadingPage';
import { ContentCard } from '../../hocs/ContentCard';
import React, { ReactNode } from 'react';
import {
	MenuItem as MenuItemListButton,
	MenuListButtonProps,
} from '../MenuListButton/MenuListButton';
import {
	LoadingWrapper,
	NoResultsWrapper,
	TableWrapper,
} from './StylovyzeTable.styles';
import StylovyzeTableBody, { TableCell } from './StylovyzeTableBody';
import StylovyzeTableFooter, {
	RowsPerPage,
	TableFooterItem,
} from './StylovyzeTableFooter';
import StylovyzeTableHeader, {
	SearchTableProps,
	StylovyzeTableHeaderProps,
} from './StylovyzeTableHeader';

export interface TableMenuItem {
	/** menu item text description */
	text: string;
	/** action to take once menu item clicked */
	onClick: () => void;
	/** allow svg icon to show to the left of the text */
	icon?: ReactNode;
	/** item disabled */
	disabled?: boolean;
	/** data-cy attribute for Cypress and Gainsight */
	dataCy?: string;
}

export interface StylovyzeTableRow {
	key: string | number;
	cells: TableCell[];
	/** custom event triggered on row click, overwrites `pathToGoWhenClicked` */
	pathToGoWhenClicked?: string;
	onClick?: () => void;
	/** if table row is selected */
	selected?: boolean;
	/** Render a MenuListButton with the buttonItems  */
	buttonItems?: MenuItemListButton[];
	/** additional props for the generated MenuListButton  */
	buttonItemProps?: Omit<MenuListButtonProps, 'menuItems'>;
	/** data-cy identifier for row, overwrites auto-generated */
	dataCy?: string;
	/** if table row is disabled */
	disabled?: boolean;
	/** tooltip to show on the row */
	tooltip?: string;
}

export interface SkeletonTableProps {
	rowCount: number;
}

export type CheckboxSelector = {
	headerCheckboxChecked: boolean;
	onHeaderCheckboxChange?: (checked: boolean) => void;
	onRowCheckboxChange?: (row: StylovyzeTableRow, checked: boolean) => void;
};

export interface StylovyzeTableProps {
	/** Header cells configuration */
	headers: StylovyzeTableHeaderProps[];
	/** Sorting configuration */
	sort?: {
		name?: string | number;
		order?: 'asc' | 'desc';
		onRequestSort: (
			event: React.MouseEvent<unknown>,
			name: string | number,
			order: 'asc' | 'desc',
		) => void;
	};
	/** Data cells in each row */
	rows: StylovyzeTableRow[];
	/** "data-cy" identifier for Cypress */
	dataCy: string;
	title?: string;
	/** items to be shown in the menu */
	menuItems?: TableMenuItem[];
	/** if there is only one item, allow override of style */
	buttonStyle?: {
		variant?: 'text' | 'outlined' | 'contained';
		color?: 'inherit' | 'primary' | 'secondary';
	};
	/** TableRow bordered on selected */
	selectedBordered?: boolean;
	/** search bar settings */
	search?: SearchTableProps;
	/** pagination settings */
	pagination?: {
		totalNumberOfPages: number;
		currentPage?: number;
		dataCy?: string;
		onChange?: (event: object, page: number) => void;
		extraInfo?: string;
	};
	/** items to display in the footer */
	footerItems?: TableFooterItem[];
	initialized?: boolean;
	loadingTitle?: string;
	minHeight?: string;
	/** rows per page title, contents and initial selected index  */
	rowsPerPage?: RowsPerPage;
	rowHeight?: string | number;
	className?: string;
	useSkeleton?: SkeletonTableProps;
	/**
	 * If menuItems are not defined then it will be rendered next to the title / search bar
	 */
	titleArea?: React.ReactNode;
	/**
	 * If footerItems and pagination are not defined then it will be rendered in the footer
	 */
	footerArea?: React.ReactNode;
	/** Message in case the table has no results  */
	noResultsArea?: React.ReactNode;
	applyBodyPadding?: boolean;
	emptyTableBody?: React.ReactNode;
	checkboxSelector?: CheckboxSelector;
}

const StylovyzeTable = ({
	sort,
	search,
	title,
	headers,
	dataCy,
	rows,
	menuItems,
	buttonStyle,
	pagination,
	footerItems,
	selectedBordered,
	initialized = true,
	loadingTitle,
	minHeight,
	rowsPerPage,
	rowHeight,
	className,
	useSkeleton,
	titleArea,
	footerArea,
	noResultsArea = '',
	applyBodyPadding = false,
	emptyTableBody,
	checkboxSelector,
}: StylovyzeTableProps): JSX.Element => {
	const handleRowClick = (path?: string, onClick?: () => void) => () => {
		if (onClick) {
			onClick();
		}
	};

	return (
		<ContentCard
			className={className}
			applyBodyPadding={applyBodyPadding}
			header={
				(title ||
					search ||
					(menuItems && menuItems.length > 0) ||
					titleArea) && (
					<StylovyzeTableHeader
						title={title}
						search={search}
						menuItems={menuItems}
						buttonStyle={buttonStyle}
						titleArea={titleArea}
					/>
				)
			}
			footer={
				(rowsPerPage || pagination || footerItems || footerArea) && (
					<StylovyzeTableFooter
						pagination={pagination}
						rowsPerPage={rowsPerPage}
						footerItems={footerItems}
						footerArea={footerArea}
					/>
				)
			}>
			<TableWrapper $minHeight={minHeight} container>
				<StylovyzeTableBody
					headers={headers}
					sort={sort}
					initialized={initialized}
					rows={rows}
					dataCy={dataCy}
					handleRowClick={handleRowClick}
					rowHeight={rowHeight}
					selectedBordered={selectedBordered}
					minHeight={minHeight}
					rowsPerPage={rowsPerPage}
					useSkeleton={useSkeleton}
					emptyTableBody={emptyTableBody}
					checkboxSelector={checkboxSelector}
				/>
				{rows.length <= 0 && initialized && (
					<NoResultsWrapper>{noResultsArea}</NoResultsWrapper>
				)}
				{!(initialized || useSkeleton) && (
					<LoadingWrapper>
						<LoadingPage title={loadingTitle} size="sm" />
					</LoadingWrapper>
				)}
			</TableWrapper>
		</ContentCard>
	);
};

export default StylovyzeTable;
